import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link as MuiLink,
  IconButton,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Images } from "../../../utils/Images";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import NewsLetter from "./newsLetter.jsx";
import { fetchCmsHomeAction } from "../../../store/slices/cmsSlice";

const Footer = () => {
  const theme = useTheme();
  const location = useLocation();
  const isLargeScreen = useMediaQuery("(min-width:1177px)");
  const isMediumScreen = useMediaQuery(
    "(min-width:768px) and (max-width:1176px)"
  );
  const isSmallScreen = useMediaQuery("(max-width:767px)");
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <=600px
  const [hasDonationBox, setHasDonationBox] = useState(false);
  const dispatch = useDispatch();
  const cmsHome = useSelector((state) => state.cms.home);

  useEffect(() => {
    if (!cmsHome) {
      dispatch(fetchCmsHomeAction());
    }
  }, [cmsHome, dispatch]);

  // Check if donation box exists on the page
  useEffect(() => {
    const checkDonationBox = () => {
      const donationBox = document.querySelector('[data-donation-box="true"]');
      setHasDonationBox(!!donationBox);
    };

    // Check initially
    checkDonationBox();

    // Set up a MutationObserver to watch for changes
    const observer = new MutationObserver(checkDonationBox);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also check on route changes
    const interval = setInterval(checkDonationBox, 500);

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [location.pathname]);

  // Hide footer only on mobile for specific routes
  const hideFooterOnMobileRoutes = [
    "/donate-once-page",
    "/donate-monthly-page",
    "/donate-product-page",
    "/overseas-donor-page",
    "/thankyou-page",
    "/monthly/thankyou-page",
    "/faq-page",
  ];

  const normalizedPathname = (location.pathname || "").toLowerCase().replace(/\/$/, "");

  const quickLinks = [
    // { name: "Life", path: "/life" },
    { name: "Explore Campaigns", path: "/campaigns" },
    { name: "Monthly Donation", path: "/monthly-donation" },
    { name: "Start New Campaign", path: "/start-campaign" },
    { name: "Blogs", path: "/blogs" },
    { name: "Contact Us", path: "/contact" },
  ];

  const informationLinks = [
    { name: "About Us", path: "/about" },
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms & Conditions", path: "/terms&Conditions" },
    { name: "Refund and Cancellation Policy", path: "/refund&cancellation" },
    { name: "Return Policy", path: "/return-policy" },
    { name: "FAQ’s", path: "/Faq-page" },
    { name: "Documents", path: "/documents" },
  ];

  const paymentLogos = [
    { name: "MasterCard", src: Images.mastercardLogo },
    { name: "Visa", src: Images.visaLogo },
    { name: "UPI", src: Images.upiLogo },
    { name: "American Express", src: Images.americanExpressLogo },
  ];

  // Social media links from CMS:
  // - Default: section id 102 ("Social Media Links")
  // - Life / life detail routes: section id 103 ("Social Media Links for Life")
  const socialLinksSectionId = (() => {
    const path = (location.pathname || "").toLowerCase();
    if (path.startsWith("/life")) return 103;
    return 102;
  })();

  const socialLinks = React.useMemo(() => {
    const sections = Array.isArray(cmsHome?.data) ? cmsHome.data : [];
    const section = sections.find((s) => s?.id === socialLinksSectionId);
    const related = Array.isArray(section?.relatedCMS) ? section.relatedCMS : [];
    return related
      .filter((r) => r?.status === 1 || r?.status === true)
      .map((r) => r?.link || "")
      .filter((link) => typeof link === "string" && link.trim().length > 0);
  }, [cmsHome, socialLinksSectionId]);

  // Keep after all hooks to avoid "Rendered fewer hooks than expected"
  if (isMobile && hideFooterOnMobileRoutes.includes(normalizedPathname)) {
    return null;
  }

  return (
    <>
      {/* Newsletter Section */}
      <Box sx={{ 
        mb: '-45px',
        zIndex: 10, 
        marginTop: '50px',
        '@media (max-width: 700px)': {
          mb: '30px',
          marginTop: '30px'
        }
      }}>
        <NewsLetter />
      </Box>
      <Box
        component="footer"
        sx={{
          bgcolor: "#151513",
          color: "#DDDDDE",
          minHeight: { xs: "auto", md: "533px" }, // reduce fixed height on small screens
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          overflow: "hidden",
          width: "100%",
          paddingBottom: hasDonationBox && isMobile ? "120px" : "0px",
          // Global link styling to override browser defaults
          "& a": {
            color: "inherit !important",
            textDecoration: "none !important",
            "&:hover": {
              textDecoration: "none !important",
            },
            "&:active": {
              textDecoration: "none !important",
            },
            "&:focus": {
              textDecoration: "none !important",
            },
            "&:visited": {
              textDecoration: "none !important",
            },
          },
        }}
      >
        {/* Decorative elements for mobile only - positioned absolutely */}
        <Box
          sx={{
            display: { xs: "block", md: "none" }, // Only show on mobile
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none", // Don't interfere with clicks
            zIndex: 1,
          }}
        >
          {/* Heart icon - top right */}
          <Box
            component="img"
            src={Images.heart}
            alt="Heart"
            sx={{
              position: "absolute",
              top:isMobile?"54px":"40px",
              right:isMobile?"105px": "75px",
              width: "12px",
              height: "24px",
              transform: "scaleX(-1) rotate(23.51deg)",
            }}
          />

          {/* Hand/wave icon - top right, slightly lower */}
          <Box
            component="img"
            src={Images.stringHand}
            alt="Hand"
            sx={{
              position: "absolute",
              top: "40px",
              right: "-10px",
              width: "65px",
              height: "55px",
              // transform: "rotate(15deg)",
            }}
          />
        </Box>
        <Container
          maxWidth={false}
          sx={{
            width: "94.44%",
            maxWidth: "1440px",
            margin: "0 auto",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            paddingTop: { xs: "40px", sm: "60px", md: "90px" }, // smaller top padding on small screens
          }}
          disableGutters
        >
          {/* Main Content */}
          <Grid
            container
            spacing={{ xs: 3, md: 0 }} // add spacing only on small screens to separate stacked items
            sx={{
              flex: 1,
              alignItems: "flex-start",
              justifyContent: "space-between",
              // Custom breakpoint handling for medium screens
              "& .MuiGrid-item": {
                // For screens between 768px and 1176px
                "@media (min-width: 768px) and (max-width: 1176px)": {
                  "&:nth-of-type(1)": {
                    // Logo section
                    width: "100%",
                    maxWidth: "100%",
                    flexBasis: "100%",
                    marginBottom: "32px",
                  },
                  "&:nth-of-type(2)": {
                    // Quick Links
                    width: "50%",
                    maxWidth: "50%",
                    flexBasis: "50%",
                  },
                  "&:nth-of-type(3)": {
                    // Information
                    width: "50%",
                    maxWidth: "50%",
                    flexBasis: "50%",
                  },
                  "&:nth-of-type(4)": {
                    // Contact Us
                    width: "100%",
                    maxWidth: "100%",
                    flexBasis: "100%",
                    marginTop: "32px",
                  },
                },
              },
            }}
          >
            {/* Logo and Description Section */}
            <Grid
              item
              xs={12}
              md={isLargeScreen ? 3.5 : 12}
              sx={{
                // Custom responsive behavior for medium screens
                "@media (min-width: 768px) and (max-width: 1176px)": {
                  order: 1,
                  marginBottom: "32px",
                },
              }}
            >
              <Box sx={{ mb: { xs: 2, sm: 3, md: 3 } }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: { xs: 2, sm: 3, md: 4 },
                  }}
                >
                  <Link
                    to="/"
                    sx={{
                      textDecoration: "none",
                      "&:hover": {
                        textDecoration: "none",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.footerLogo}
                      alt="True Hope Foundation"
                      sx={{ 
                        height: { xs: 50, sm: 50, md: 50 },
                        cursor: "pointer",
                        transition: "opacity 0.3s ease",
                        "&:hover": {
                          opacity: 0.8,
                        },
                      }}
                    />
                  </Link>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#DDDDDE",
                    lineHeight: 1.6,
                    mb: { xs: 2, sm: 3, md: 4 },
                    fontSize: { xs: "12px", sm: "13px", md: "14px" },
                    fontFamily: '"Red Hat Display", sans-serif',
                    maxWidth: { xs: "100%", md: "378px" },
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                  }}
                >
                  We're India's most trusted platform, dedicated to fostering
                  social impact. Our unique model allows donors to contribute
                  funds for essential items needed by Indian NGOs and charities
                  with us.
                </Typography>

                {/* Social Media Icons (desktop/tablet) */}
                <Stack
                  direction="row"
                  spacing={{ xs: 1.5, sm: 2 }}
                  sx={{
                    display: { xs: "none", md: "flex" },
                    // Hide on medium screens, show on large screens
                    "@media (min-width: 768px) and (max-width: 1176px)": {
                      display: "none",
                    },
                  }}
                >
                  <IconButton
                    disabled={!socialLinks[0]}
                    onClick={() => socialLinks[0] && window.open(socialLinks[0], "_blank")}
                    sx={{
                      border: "1px solid #4A4A49",
                      borderRadius: "10px",
                      color: "white",
                      width: { xs: 40, sm: 44, md: 40 },
                      height: { xs: 40, sm: 44, md: 40 },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderColor: "#6A6A69",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.footerFacebook}
                      alt="Facebook"
                      sx={{
                        width: { xs: 20, sm: 22, md: 24 },
                        height: { xs: 20, sm: 22, md: 24 },
                      }}
                    />
                  </IconButton>
                  <IconButton
                    disabled={!socialLinks[1]}
                    onClick={() => socialLinks[1] && window.open(socialLinks[1], "_blank")}
                    sx={{
                      border: "1px solid #4A4A49",
                      borderRadius: "10px",
                      color: "white",
                      width: { xs: 40, sm: 44, md: 40 },
                      height: { xs: 40, sm: 44, md: 40 },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderColor: "#6A6A69",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.footerLinkedin}
                      alt="LinkedIn"
                      sx={{
                        width: { xs: 20, sm: 22, md: 24 },
                        height: { xs: 20, sm: 22, md: 24 },
                      }}
                    />
                  </IconButton>
                  <IconButton
                    disabled={!socialLinks[2]}
                    onClick={() => socialLinks[2] && window.open(socialLinks[2], "_blank")}
                    sx={{
                      border: "1px solid #4A4A49",
                      borderRadius: "10px",
                      color: "white",
                      width: { xs: 40, sm: 44, md: 40 },
                      height: { xs: 40, sm: 44, md: 40 },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderColor: "#6A6A69",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.footerInstagram}
                      alt="Instagram"
                      sx={{
                        width: { xs: 20, sm: 22, md: 24 },
                        height: { xs: 20, sm: 22, md: 24 },
                      }}
                    />
                  </IconButton>
                </Stack>
              </Box>
            </Grid>

            {/* Quick Links */}
            <Grid
              item
              xs={12}
              sm={6}
              md={isLargeScreen ? 2.5 : 6}
              sx={{
                "@media (min-width: 768px) and (max-width: 1176px)": {
                  order: 2,
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  fontFamily: '"Red Hat Display", sans-serif',
                }}
              >
                Quick Links
              </Typography>
              <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }} sx={{fontSize: { xs: "12px", sm: "15px", md: "16px" }}}>
                {quickLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.path}
                    sx={{
                      color: "#DDDDDE !important",
                      textDecoration: "none !important",
                      fontSize: { xs: "13px", sm: "15px", md: "16px" },
                      fontFamily: '"Red Hat Display", sans-serif',
                      display: "block",
                      transition: "color 0.3s ease",
                      "&:hover": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:active": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:focus": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:visited": {
                        color: "#DDDDDE !important",
                        textDecoration: "none !important",
                      },
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Stack>
            </Grid>

            {/* Information */}
            <Grid
              item
              xs={12}
              sm={6}
              md={isLargeScreen ? 2.5 : 6}
              sx={{
                "@media (min-width: 768px) and (max-width: 1176px)": {
                  order: 3,
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  fontFamily: '"Red Hat Display", sans-serif',
                }}
              >
                Information
              </Typography>
              <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }} sx={{fontSize: { xs: "12px", sm: "15px", md: "16px" }}}>
                {informationLinks.map((link, index) => (
                  <Link
                    key={index}
                    to={link.path}
                    sx={{
                      color: "#DDDDDE !important",
                      textDecoration: "none !important",
                      fontSize: { xs: "13px", sm: "15px", md: "16px" },
                      fontFamily: '"Red Hat Display", sans-serif',
                      display: "block",
                      transition: "color 0.3s ease",
                      "&:hover": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:active": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:focus": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:visited": {
                        color: "#DDDDDE !important",
                        textDecoration: "none !important",
                      },
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Stack>
            </Grid>

            {/* Contact Us */}
            <Grid
              item
              xs={12}
              md={isLargeScreen ? 3.5 : 12}
              sx={{
                "@media (min-width: 768px) and (max-width: 1176px)": {
                  order: 4,
                  marginTop: "32px",
                },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "white",
                  fontWeight: 700,
                  mb: { xs: 2, sm: 2.5, md: 3 },
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  fontFamily: '"Red Hat Display", sans-serif',
                }}
              >
                Contact Us
              </Typography>
              <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                <MuiLink
                  href="https://www.google.com/maps/search/?api=1&query=C-79+Shankar+Nagar,+Nandanwan+Jodhpur+Rajasthan+INDIA+342008"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#DDDDDE !important",
                    textDecoration: "none !important",
                    fontSize: { xs: "12px", sm: "15px", md: "16px" },
                    lineHeight: 1.5,
                    fontFamily: '"Red Hat Display", sans-serif',
                    maxWidth: { xs: "280px", md: "370px" },
                    display: "block",
                    transition: "color 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      color: "white !important",
                      textDecoration: "none !important",
                    },
                    "&:active": {
                      color: "white !important",
                      textDecoration: "none !important",
                    },
                    "&:focus": {
                      color: "white !important",
                      textDecoration: "none !important",
                    },
                    "&:visited": {
                      color: "#DDDDDE !important",
                      textDecoration: "none !important",
                    },
                  }}
                >
                  C-79 Shankar Nagar, Nandanwan Jodhpur Rajasthan INDIA 342008
                </MuiLink>

                {/* Email with icon (mobile shows icon) */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={{ xs: 1.5, sm: 2, md: 0 }}
                >
                  <Box
                    sx={{
                      display: { xs: "inline-flex", md: "none" },
                      color: "#DDDDDE",
                    }}
                  >
                    <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <MuiLink
                    href="mailto:contact@truehopefoundation.in"
                    sx={{
                      color: "#DDDDDE !important",
                      textDecoration: "none !important",
                      fontSize: { xs: "13px", sm: "15px", md: "16px" },
                      fontFamily: '"Red Hat Display", sans-serif',
                      display: "block",
                      transition: "color 0.3s ease",
                      "&:hover": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:active": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:focus": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:visited": {
                        color: "#DDDDDE !important",
                        textDecoration: "none !important",
                      },
                    }}
                  >
                    contact@truehopefoundation.in
                  </MuiLink>
                </Stack>

                {/* Phone with icon (mobile shows icon) */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={{ xs: 1.5, sm: 2, md: 0 }}
                >
                  <Box
                    sx={{
                      display: { xs: "inline-flex", md: "none" },
                      color: "#DDDDDE",
                    }}
                  >
                    <LocalPhoneOutlinedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <MuiLink
                    href="tel:+918282825582"
                    sx={{
                      color: "#DDDDDE !important",
                      textDecoration: "none !important",
                      fontSize: { xs: "13px", sm: "15px", md: "16px" },
                      fontFamily: '"Red Hat Display", sans-serif',
                      display: "block",
                      transition: "color 0.3s ease",
                      "&:hover": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:active": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:focus": {
                        color: "white !important",
                        textDecoration: "none !important",
                      },
                      "&:visited": {
                        color: "#DDDDDE !important",
                        textDecoration: "none !important",
                      },
                    }}
                  >
                    +91 8282825582
                  </MuiLink>
                </Stack>

                {/* Social Media Icons (mobile and medium) — same CMS links as desktop; no Twitter */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{
                    display: { xs: "flex", md: "none" },
                    pt: 1,
                    "@media (min-width: 768px) and (max-width: 1176px)": {
                      display: "flex",
                    },
                  }}
                >
                  <IconButton
                    disabled={!socialLinks[0]}
                    onClick={() => socialLinks[0] && window.open(socialLinks[0], "_blank")}
                    sx={{
                      border: "1px solid #4A4A49",
                      borderRadius: "10px",
                      color: "white",
                      width: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderColor: "#6A6A69",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.footerFacebook}
                      alt="Facebook"
                      sx={{ width: 20, height: 20 }}
                    />
                  </IconButton>
                  <IconButton
                    disabled={!socialLinks[1]}
                    onClick={() => socialLinks[1] && window.open(socialLinks[1], "_blank")}
                    sx={{
                      border: "1px solid #4A4A49",
                      borderRadius: "10px",
                      color: "white",
                      width: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderColor: "#6A6A69",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.footerLinkedin}
                      alt="LinkedIn"
                      sx={{ width: 20, height: 20 }}
                    />
                  </IconButton>
                  <IconButton
                    disabled={!socialLinks[2]}
                    onClick={() => socialLinks[2] && window.open(socialLinks[2], "_blank")}
                    sx={{
                      border: "1px solid #4A4A49",
                      borderRadius: "10px",
                      color: "white",
                      width: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderColor: "#6A6A69",
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.footerInstagram}
                      alt="Instagram"
                      sx={{ width: 20, height: 20 }}
                    />
                  </IconButton>
                </Stack>
              </Stack>

              {/* Payment Methods */}
              <Box sx={{ mt: { xs: 4, sm: 5, md: 6 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    mb: { xs: 2, sm: 2.5, md: 3 },
                    fontSize: { xs: "16px", sm: "18px", md: "20px" },
                    fontFamily: '"Red Hat Display", sans-serif',
                  }}
                >
                  We Accept
                </Typography>
                <Stack direction="row" spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
                  {paymentLogos.map((payment, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: { xs: 36, sm: 40, md: 45 },
                        height: { xs: 20, sm: 22, md: 25 },
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        component="img"
                        src={payment.src}
                        alt={payment.name}
                        sx={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* Footer Bottom */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "1440px",
            margin: "0 auto",
            mt: { xs: "24px", sm: "28px", md: "32px" },
            mb: { xs: "0px", sm: "0", md: "32px" },
            bgcolor: { xs: "#F04951", md: "transparent" },
          }}
        >
          <Divider
            sx={{
              borderColor: "#404040",
              mb: { xs: 2, sm: 3, md: 4 },
            }}
          />
          {/* Red bar only on mobile */}
          <Box
            sx={{
              bgcolor: { xs: "#F04951", md: "transparent" },
              py: { xs: 1.5, sm: 1.75, md: 0 },
              mb: { xs: "12px", sm: "14", md: "0" },
            }}
          >
            <Typography
              variant="body2"
              align="center"
              sx={{
                color: { xs: "#FFFFFF", md: "#DDDDDE" },
                fontSize: { xs: "12px", sm: "13px", md: "14px" },
                letterSpacing: "0.5px",
                fontFamily: '"Red Hat Display", sans-serif',
              }}
            >
              © {new Date().getFullYear()} True Hope Foundation All Rights Reserved
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Footer;
